import { useState } from "react";
import {
  CheckCircle2,
  ChevronRight,
  CalendarCheck,
  Clock,
  StickyNote,
  MessageSquare,
  FileText,
} from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { updateActivity } from "../../../lib/queries";
import { computeScore, getLevel, levelToneClass } from "../../../lib/scoring";
import { useToast } from "../../../components/Toast";
import ConfirmDeleteDrawer from "../../../components/ConfirmDeleteDrawer";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function safeParse(response) {
  if (!response) return {};
  if (typeof response === "object") return response;
  try {
    return JSON.parse(response);
  } catch {
    return {};
  }
}

// Solo se muestra el `response` en crudo si es una string legible (yesno/text).
// Oculta el JSON de un formulario structured "dormido" (empieza por { o [) o
// cualquier valor que no sea texto plano.
function isReadableTextResponse(response) {
  if (typeof response !== "string") return false;
  const trimmed = response.trim();
  if (!trimmed) return false;
  return trimmed[0] !== "{" && trimmed[0] !== "[";
}

function parseStructuredResponse(response, config) {
  if (!response || !config?.fields) return null;
  try {
    const values = typeof response === "string" ? JSON.parse(response) : response;
    return config.fields
      .filter((f) => f.type !== "heading")
      .map((f) => {
        const raw = values[f.id] || "";
        let display = raw || "—";
        let isFile = false;
        let fileInfo = null;
        if (f.type === "file" && raw) {
          try {
            fileInfo = JSON.parse(raw);
            display = fileInfo.name;
            isFile = true;
          } catch { /* not valid JSON, show raw */ }
        }
        return { label: f.label, value: display, isFile, fileInfo };
      });
  } catch {
    return null;
  }
}

function ActivityRow({ activity, onToggle, busy }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = activity.completed && (activity.response || activity.notes);
  const isStructured = activity.input_type === "structured";
  const structuredFields = isStructured
    ? parseStructuredResponse(activity.response, activity.config)
    : null;
  const score =
    isStructured && activity.config?.fields
      ? computeScore(activity.config.fields, safeParse(activity.response))
      : null;
  const level = score ? getLevel(score.total, activity.config?.levels) : null;

  return (
    <div>
      <div
        onClick={() => hasDetail && setExpanded(!expanded)}
        className={`px-4 py-2.5 flex items-center gap-3 ${
          activity.completed ? "bg-emerald-50/50" : ""
        } ${hasDetail ? "cursor-pointer hover:bg-gray-50" : ""}`}
      >
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
            activity.completed
              ? "border-emerald-500 bg-emerald-500 text-white"
              : "border-gray-300"
          }`}
        >
          {activity.completed && <CheckCircle2 size={12} strokeWidth={3} />}
        </div>
        <span
          className={`text-sm flex-1 ${
            activity.completed ? "text-emerald-700" : "text-gray-700"
          }`}
        >
          {activity.name}
        </span>
        {activity.admin_only && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(activity);
            }}
            disabled={busy}
            className={`text-xs font-medium px-2 py-1 rounded-lg border transition-colors shrink-0 disabled:opacity-50 ${
              activity.completed
                ? "text-gray-500 border-gray-200 hover:bg-gray-50"
                : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            }`}
          >
            {activity.completed ? "Desmarcar" : "Marcar"}
          </button>
        )}
        {hasDetail && (
          <ChevronRight
            size={14}
            className={`text-gray-400 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          />
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-3 pl-12 space-y-2 bg-gray-50/50">
          {activity.completed_at && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <CalendarCheck size={12} className="text-emerald-500" />
              Completada: {formatDate(activity.completed_at)}
            </div>
          )}
          {activity.updated_at &&
            activity.completed_at &&
            new Date(activity.updated_at) > new Date(activity.completed_at) && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Clock size={12} className="text-blue-500" />
                Editada: {formatDate(activity.updated_at)}
              </div>
            )}

          {structuredFields ? (
            <div className="space-y-1.5 mt-1">
              {structuredFields.map((f, i) => (
                <div key={i}>
                  <span className="text-xs font-medium text-gray-500">
                    {f.label}:
                  </span>
                  {f.isFile && f.fileInfo ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const { data } = await supabase.storage
                          .from("activity-files")
                          .createSignedUrl(f.fileInfo.path, 60);
                        if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                      }}
                      className="flex items-center gap-1.5 text-sm text-brand hover:underline"
                    >
                      <FileText size={14} />
                      {f.value}
                    </button>
                  ) : (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {f.value}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : isReadableTextResponse(activity.response) ? (
            <div className="flex items-start gap-1.5 mt-1">
              <MessageSquare size={12} className="text-indigo-400 mt-0.5" />
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {activity.response}
              </p>
            </div>
          ) : null}

          {score && score.scaleCount > 0 && (
            <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-white space-y-1">
                {score.groups.map((g, i) => (
                  <div key={i} className="flex justify-between text-xs text-gray-500">
                    <span>{g.label}</span>
                    <span className="font-medium text-gray-700">
                      {g.subtotal} / {g.max}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-semibold text-gray-900 pt-1 border-t border-gray-100">
                  <span>Total</span>
                  <span>
                    {score.total} / {score.max}
                  </span>
                </div>
              </div>
              {level && (
                <div className={`px-3 py-2 border-t text-xs font-semibold ${levelToneClass(level.tone)}`}>
                  {level.label}
                </div>
              )}
            </div>
          )}

          {activity.notes && (
            <div className="flex items-start gap-1.5 mt-1">
              <StickyNote size={12} className="text-amber-500 mt-0.5" />
              <p className="text-sm text-amber-800 whitespace-pre-wrap">
                {activity.notes}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ActivitiesTab({ activities, studentId, studentName, onRefetch }) {
  const { showError, showSuccess } = useToast();
  const [confirming, setConfirming] = useState(null); // actividad en confirmación
  const [saving, setSaving] = useState(false);

  const grouped = activities.reduce((acc, act) => {
    if (!acc[act.block_name]) acc[act.block_name] = [];
    acc[act.block_name].push(act);
    return acc;
  }, {});

  const handleConfirm = async () => {
    if (!confirming || saving) return;
    const target = confirming;
    const willComplete = !target.completed;
    setSaving(true);
    try {
      await updateActivity(studentId, target.id, {
        completed: willComplete,
        completed_at: willComplete ? new Date().toISOString() : null,
      });
      setConfirming(null);
      showSuccess(
        willComplete ? "Tarea marcada como completada" : "Tarea desmarcada"
      );
      onRefetch?.();
    } catch (err) {
      setConfirming(null);
      showError(err.message || "No se pudo actualizar la tarea");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([blockName, blockActs]) => {
        const completed = blockActs.filter((a) => a.completed).length;
        const pct =
          blockActs.length > 0
            ? Math.round((completed / blockActs.length) * 100)
            : 0;

        return (
          <details
            key={blockName}
            className="border border-gray-200 rounded-xl overflow-hidden group"
          >
            <summary className="px-4 py-3 bg-gray-50 flex justify-between items-center cursor-pointer hover:bg-gray-100">
              <span className="font-medium text-gray-900">{blockName}</span>
              <div className="flex items-center gap-3">
                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-sm text-gray-500 w-16 text-right">
                  {completed}/{blockActs.length}
                </span>
                <ChevronRight
                  size={16}
                  className="text-gray-400 group-open:rotate-90 transition-transform"
                />
              </div>
            </summary>
            <div className="divide-y divide-gray-100">
              {blockActs
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((act) => (
                  <ActivityRow
                    key={act.id}
                    activity={act}
                    onToggle={setConfirming}
                    busy={saving}
                  />
                ))}
            </div>
          </details>
        );
      })}

      {confirming && (
        <ConfirmDeleteDrawer
          title={confirming.completed ? "Desmarcar asistencia" : "Confirmar asistencia"}
          itemName={studentName || confirming.name}
          message={
            confirming.completed
              ? "Vas a desmarcar esta tarea. El siguiente bloque volverá a bloquearse. El progreso que el alumno ya tenga en él NO se borra."
              : "Vas a marcar esta tarea como completada en nombre del alumno. Se desbloqueará el siguiente bloque de la ruta."
          }
          confirmLabel={confirming.completed ? "Desmarcar" : "Marcar como completada"}
          onConfirm={handleConfirm}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}
